import React, {useCallback, useEffect, useState} from "react";
import {useLoaderData, useParams, useSearchParams} from "react-router";
import {format, isValid, parse} from "date-fns";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import {useQuery} from "@tanstack/react-query";
import {FaPlus, FaPrint} from "react-icons/fa";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import ScheduleAttendanceService from "../../../../services/dispatch/schedule-attendance-service.js";
import RegisteredLocation from "../../../../components/registered-location/registered-location.jsx";
import ScheduleAttendanceActionsCard from "./components/schedule-attendance-actions-card.jsx";
import ScheduleAttendanceAttendanceCard from "./components/schedule-attendance-attendance-card.jsx";
import ScheduleAttendanceDataCard from "./components/schedule-attendance-data-card.jsx";
import ScheduleAttendanceProcedureModal from "./components/schedule-attendance-procedure-modal.jsx";
import ScheduleAttendanceScheduleCard from "./components/schedule-attendance-schedule-card.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const BASENAME = import.meta.env.BASE_URL;
const MODAL_CLOSE_ANIMATION_MS = 250;

const weekdayOptions = [
    {value: "1", label: "Segunda"},
    {value: "2", label: "Terça"},
    {value: "3", label: "Quarta"},
    {value: "4", label: "Quinta"},
    {value: "5", label: "Sexta"},
    {value: "6", label: "Sábado"},
    {value: "7", label: "Domingo"},
];

const initialForm = {
    name: "",
    reason: null,
    priority: null,
    channel: null,
    contactName: "",
    contactPhone: "",
    description: "",
    inactive: false,
    inactiveObservation: "",
    systemUpdateDate: "",
    userCreated: "",
    userUpdated: "",
    systemCreationDate: "",
    systemUpdateDateView: "",
};

const initialAddress = {
    place: "",
    placeId: "",
    type_place: "",
    street: "",
    number: "",
    neighborhood: "",
    crossroads: "",
    reference_point: "",
    city: "",
    latitude: "",
    longitude: "",
    sectors: [],
};

const initialRecurrence = {
    dateStart: "",
    dateEnd: "",
    startHour: "",
    weekdays: [],
};

const normalizeOption = (item) => ({
    ...item,
    value: item?.value ?? item?.id ?? item?.name ?? item?.text,
    label: item?.label ?? item?.text ?? item?.name ?? item?.id ?? item?.value,
});

const optionForValue = (options = [], value) => {
    if (value == null || value === "") return null;
    if (typeof value === "object" && value.value !== undefined && value.label !== undefined) return value;

    const normalized = String(value);
    return options.find(option => String(option.value) === normalized)
        ?? {value, label: value};
};

const optionValue = (option) => {
    if (option == null || option === "") return "";
    if (typeof option === "object") return option.value ?? "";
    return option;
};

const parseArrayValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
        } catch {
            return value.includes("|") ? value.split("|").map(item => item.trim()) : [value];
        }
    }

    return [value];
};

const optionArrayForValue = (options = [], value) => parseArrayValue(value)
    .map((item) => {
        const rawValue = item?.value ?? item?.id ?? item?.name ?? item?.text ?? item;
        const rawLabel = item?.label ?? item?.text ?? item?.name ?? rawValue;
        return optionForValue(options, rawValue) ?? {value: rawValue, label: rawLabel};
    })
    .filter(Boolean);

const normalizeReasonOptions = (items = []) => items.map((item) => ({
    ...normalizeOption({
        ...item,
        value: item?.name ?? item?.alias ?? item?.id ?? item?.value,
        label: item?.alias ?? item?.text ?? item?.name ?? item?.label,
    }),
    reasonId: item?.id,
    attendanceProcedure: !!item?.attendanceProcedure,
}));

const normalizeNameOptions = (items = []) => items.map((item) => normalizeOption({
    ...item,
    value: item?.name ?? item?.id ?? item?.value,
    label: item?.name ?? item?.text ?? item?.label,
}));

const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && isValid(value)) return value;

    const candidates = [
        "dd/MM/yyyy HH:mm:ss",
        "dd/MM/yyyy HH:mm",
        "dd/MM/yyyy",
        "yyyy-MM-dd HH:mm:ss",
        "yyyy-MM-dd HH:mm",
        "yyyy-MM-dd",
        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
        "yyyy-MM-dd'T'HH:mm:ssXXX",
    ];

    for (const pattern of candidates) {
        const parsed = parse(String(value), pattern, new Date());
        if (isValid(parsed)) return parsed;
    }

    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
};

const formatDateTime = (value) => {
    const parsed = parseDateValue(value);
    return parsed ? format(parsed, "dd/MM/yyyy HH:mm") : "";
};

const formatDateOnly = (value) => {
    const parsed = parseDateValue(value);
    return parsed ? format(parsed, "dd/MM/yyyy") : "";
};

const formatSystemDate = (value) => {
    if (!value) return "";
    if (/^\d{2}\/\d{2}\/\d{4}/.test(String(value))) return value;
    return formatDateTime(value);
};

const getExtensionFromName = (name = "") => {
    const parts = String(name).split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const normalizeLegacyAttachment = (item) => {
    const attachId = item?.id ?? item?.attachId;
    const extension = item?.extension ?? item?.type ?? getExtensionFromName(item?.name);

    return {
        ...item,
        attachId,
        extension,
        type: extension,
        legacy: true,
        legacyOriginal: item,
        new: true,
        url: ScheduleAttendanceService.attachmentUrl(attachId),
    };
};

const normalizeDateRows = (dates = []) => {
    const rows = dates.map((item) => {
        const value = typeof item === "string" ? item.trim() : item?.date;
        return {date: formatDateTime(value) || value};
    }).filter(item => item.date);

    return [...new Map(rows.map(item => [item.date, item])).values()]
        .sort((a, b) => (parseDateValue(a.date)?.getTime() ?? 0) - (parseDateValue(b.date)?.getTime() ?? 0));
};

const hourInMinutes = (value) => {
    if (!value) return null;
    return value.split(":").reduce((total, time, index) => total + (index === 0 ? Number(time) * 60 : Number(time)), 0);
};

const validateForm = (form, scheduleMode, dateList, recurrence) => {
    if (!form.name) return "O campo \"Nome\" é obrigatório.";
    if (!optionValue(form.reason)) return "O campo \"Natureza\" é obrigatório.";
    if (!optionValue(form.channel)) return "O campo \"Canal\" é obrigatório.";
    if (!form.description) return "O campo \"Descrição\" é obrigatório.";
    if (form.inactive && !form.inactiveObservation) return "O campo \"Motivo da inativação\" é obrigatório.";

    if (scheduleMode === "LIST" && !dateList.length) {
        return "Informe pelo menos uma data e horário para o agendamento.";
    }

    if (scheduleMode === "RECURRENCE") {
        if (!recurrence.dateStart) return "O campo \"Data inicial\" é obrigatório.";
        if (!recurrence.startHour) return "O campo \"Horário\" é obrigatório.";
        if (!recurrence.weekdays.length) return "O campo \"Dias da semana\" é obrigatório.";

        const start = parseDateValue(recurrence.dateStart);
        const end = parseDateValue(recurrence.dateEnd);
        if (start && end && start > end) {
            return "Data inicial não pode ser maior que a final.";
        }
    }

    return "";
};

const ScheduleAttendanceForm = () => {
    const navigate = navigateHook();
    const loaderData = useLoaderData();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const copyId = searchParams.get("cp");
    const sourceId = id ?? copyId;
    const isCopy = !id && !!copyId;

    const [form, setForm] = useState(initialForm);
    const [address, setAddress] = useState(initialAddress);
    const [scheduleMode, setScheduleMode] = useState("LIST");
    const [dateList, setDateList] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [dateHour, setDateHour] = useState("");
    const [recurrence, setRecurrence] = useState(initialRecurrence);
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({});
    const [attendanceList, setAttendanceList] = useState([]);
    const [procedureModal, setProcedureModal] = useState({open: false, content: ""});
    const [optionsData, setOptionsData] = useState({reasons: [], priorities: [], channels: [], boName: loaderData?.bo_name ?? "BO", sizeMaxText: "20MB"});
    const [isSaving, setIsSaving] = useState(false);

    const reasonOptions = optionsData.reasons;
    const priorityOptions = optionsData.priorities;
    const channelOptions = optionsData.channels;

    useEffect(() => {
        if (procedureModal.open || !procedureModal.content) return;

        const timeoutId = window.setTimeout(() => {
            setProcedureModal(prev => prev.open ? prev : {...prev, content: ""});
        }, MODAL_CLOSE_ANIMATION_MS);

        return () => window.clearTimeout(timeoutId);
    }, [procedureModal.open, procedureModal.content]);

    const setFormField = (field, value) => setForm(prev => ({...prev, [field]: value}));
    const setRecurrenceField = (field, value) => setRecurrence(prev => ({...prev, [field]: value}));
    const toggleInactive = (checked) => setForm(prev => ({
        ...prev,
        inactive: checked,
        inactiveObservation: checked ? prev.inactiveObservation : "",
    }));

    const resetForm = useCallback((nextOptions = optionsData) => {
        setForm({
            ...initialForm,
            priority: nextOptions.priorities.find(item => item.priorityDefault) ?? null,
            channel: nextOptions.channels.find(item => item.channelDefault) ?? null,
        });
        setAddress(initialAddress);
        setScheduleMode("LIST");
        setDateList([]);
        setSelectedDates([]);
        setDateHour("");
        setRecurrence(initialRecurrence);
        setAttaches([]);
        setAttachesDescription({});
        setAttendanceList([]);
    }, [optionsData]);

    const applyInitialData = useCallback(async () => {
        const data = sourceId
            ? await ScheduleAttendanceService.get(sourceId).catch(() => null)
            : await ScheduleAttendanceService.get(sourceId);

        if (sourceId && !data) {
            navigate("/404");
            return;
        }

        const schedule = data?.schedule ?? {};
        const reasons = normalizeReasonOptions(data?.reasons ?? []);
        const priorities = normalizeNameOptions(data?.priorities ?? []);
        const channels = normalizeNameOptions(data?.channels ?? []);

        const nextOptions = {
            reasons,
            priorities,
            channels,
            boName: data?.boName ?? loaderData?.bo_name ?? "BO",
            sizeMaxText: data?.sizeMaxText ?? "20MB",
        };

        setOptionsData(nextOptions);

        const priorityDefault = schedule.priority ?? data?.priorityDefault ?? priorities.find(item => item.priorityDefault)?.value ?? "";
        const channelDefault = schedule.channel ?? data?.channelDefault ?? channels.find(item => item.channelDefault)?.value ?? "";

        setForm({
            ...initialForm,
            name: schedule.name ?? "",
            reason: optionForValue(reasons, schedule.reason),
            priority: optionForValue(priorities, priorityDefault),
            channel: optionForValue(channels, channelDefault),
            contactName: schedule.contactName ?? "",
            contactPhone: projectUtils.formatPhone(schedule.contactPhone) ?? "",
            description: schedule.description ?? "",
            inactive: !!schedule.inactive,
            inactiveObservation: schedule.inactiveObservation ?? "",
            systemUpdateDate: schedule.systemUpdateDate ?? data?.systemUpdate ?? "",
            userCreated: id ? data?.userCreated ?? "" : "",
            userUpdated: id ? data?.userUpdated ?? "" : "",
            systemCreationDate: id ? formatSystemDate(schedule.systemCreationDate) : "",
            systemUpdateDateView: id ? formatSystemDate(schedule.systemUpdateDate) : "",
        });

        const nextAddress = {
            place: schedule.place ?? "",
            placeId: "",
            type_place: schedule.placeType ?? "",
            street: schedule.street ?? "",
            number: schedule.number ?? "",
            neighborhood: schedule.neighborhood ?? "",
            crossroads: schedule.crossRoads ?? schedule.crossroads ?? "",
            reference_point: schedule.referencePoint ?? "",
            city: schedule.city ?? data?.entityCity ?? "",
            latitude: schedule.latitude ?? "",
            longitude: schedule.longitude ?? "",
            sectors: parseArrayValue(schedule.sectors),
        };

        const dates = schedule.dates ?? {};
        const mode = dates.type ?? "LIST";

        setScheduleMode(mode);

        if (mode === "LIST") {
            setDateList(normalizeDateRows(dates.dateList ?? []));
        } else {
            const recurrenceData = dates.recurrence ?? {};
            setRecurrence({
                dateStart: formatDateOnly(recurrenceData.start),
                dateEnd: recurrenceData.end ? formatDateOnly(recurrenceData.end) : "",
                startHour: recurrenceData.hour ?? "",
                weekdays: optionArrayForValue(weekdayOptions, recurrenceData.weekdays?.map(String) ?? []),
            });
        }

        setAttendanceList(isCopy ? [] : data?.attendanceList ?? []);

        const attachments = (isCopy ? [] : (schedule.attachment ?? [])).map(normalizeLegacyAttachment);

        setAttaches(attachments);
        setAttachesDescription(attachments.reduce((acc, attach) => {
            if (attach.attachId) {
                acc[attach.attachId] = attach.description ?? "";
            }

            return acc;
        }, {}));

        if (!sourceId) {
            resetForm(nextOptions);
        }

        setAddress(nextAddress);

        return data;
    }, [id, isCopy, loaderData?.bo_name, resetForm, sourceId]);

    const {isLoading: isQueryLoading} = useQuery({
        queryFn: applyInitialData,
        queryKey: ["dispatch-schedule-attendance-form", sourceId ?? "new"],
    });

    const removeDate = useCallback((date) => {
        setDateList(prev => prev.filter(item => item.date !== date));
    }, []);

    const addSelectedDates = () => {
        if (!dateHour || !selectedDates.length) {
            projectUtils.swalErrorMessage("", "Informe pelo menos uma data e o horário.", "Ok");
            return;
        }

        const nextDates = selectedDates.map((date) => {
            const [hour = "0", minute = "0"] = dateHour.split(":");
            const scheduled = new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(hour), Number(minute));
            return {date: format(scheduled, "dd/MM/yyyy HH:mm")};
        });

        setDateList(prev => normalizeDateRows([...prev, ...nextDates]));
        setSelectedDates([]);
        setDateHour("");
    };

    const buildFormData = () => {
        const formData = new FormData();
        const attachmentDescriptionNew = [];

        attaches.forEach((attach) => {
            const description = attachesDescription[attach.attachId];
            if (description) {
                attachmentDescriptionNew.push({name: attach.name, description});
            }

            formData.append("attachFiles", attach);
        });

        const dates = scheduleMode === "LIST"
            ? {type: scheduleMode, dateList: dateList.map(item => item.date), recurrence: undefined}
            : {
                type: scheduleMode,
                dateList: undefined,
                recurrence: {
                    start: recurrence.dateStart,
                    end: recurrence.dateEnd,
                    hour: recurrence.startHour,
                    hourInMinutes: hourInMinutes(recurrence.startHour),
                    weekdays: recurrence.weekdays.map(item => item.value),
                },
            };

        const attachment = attaches
            .filter(attach => attach.legacy)
            .map(attach => ({
                ...attach.legacyOriginal,
                description: attachesDescription[attach.attachId] ?? attach.legacyOriginal?.description ?? null,
            }));

        const schedule = {
            systemUpdateDate: form.systemUpdateDate,
            name: form.name,
            reason: optionValue(form.reason),
            priority: optionValue(form.priority),
            channel: optionValue(form.channel),
            contactName: form.contactName,
            contactPhone: String(form.contactPhone ?? "").replace(/[^0-9]+/g, ""),
            description: form.description,
            city: address.city,
            placeType: address.type_place,
            place: address.place,
            neighborhood: address.neighborhood,
            street: address.street,
            crossroads: address.crossroads,
            referencePoint: address.reference_point,
            latitude: address.latitude || null,
            longitude: address.longitude || null,
            number: address.number,
            sectors: parseArrayValue(address.sectors).map(item => optionValue(item)),
            dates,
            inactive: form.inactive,
            inactiveObservation: form.inactive ? form.inactiveObservation : "",
            attachment,
            attachmentDescription: attachmentDescriptionNew.length ? attachmentDescriptionNew : null,
        };

        if (id) schedule.id = id;

        formData.append("myModel", JSON.stringify(schedule));
        return formData;
    };

    const persist = (addAfter = false, copy = false) => {
        const message = validateForm(form, scheduleMode, dateList, recurrence);
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        setIsSaving(true);
        const request = id
            ? ScheduleAttendanceService.update(id, buildFormData())
            : ScheduleAttendanceService.save(buildFormData());

        request
            .then((data) => {
                projectUtils.success(true);

                if (addAfter) {
                    resetForm();
                    navigate(API_DESPACHO_WEB + "/schedule-attendance/form", false);
                    return;
                }

                if (copy) {
                    navigate(
                        `${API_DESPACHO_WEB}/schedule-attendance/form?cp=${data.id}`,
                        false,
                    );
                    return;
                }

                navigate(API_DESPACHO_WEB + "/schedule-attendance", false);
            })
            .catch((error) => {
                console.error(error);
                const message = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", message, "Ok");
            })
            .finally(() => setIsSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o agendamento "${form.name}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                ScheduleAttendanceService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/schedule-attendance", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const message = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", message, "Ok");
                    });
            });
    };

    const newSchedule = () => navigate(
        API_DESPACHO_WEB + "/schedule-attendance/form",
        false,
    );

    const printSchedule = () => window.open(`${BASENAME}reports/schedule_attendance_info?id=${id}`, "_blank");

    const loadProcedure = () => {
        const reasonId = form.reason?.reasonId;
        if (!reasonId) return;

        ScheduleAttendanceService.getAttendanceProcedure(reasonId)
            .then((content) => setProcedureModal({open: true, content}))
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível carregar o modelo.", "Ok");
            });
    };

    const copyProcedureToDescription = () => {
        setForm(prev => ({
            ...prev,
            description: [prev.description, procedureModal.content].filter(Boolean).join(prev.description ? "\n\n" : ""),
        }));
        setProcedureModal(prev => ({...prev, open: false}));
    };

    const isLoading = isQueryLoading || isSaving;
    const hasProcedure = !!form.reason?.attendanceProcedure;

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title browserTitle={'Agendamento'}>Agendamento {id ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {id &&
                    <div className="flex gap-2">
                        <Button title="Imprimir" onClick={printSchedule}><FaPrint/></Button>
                        <Button target="primary-outline" onClick={newSchedule}><FaPlus/>Adicionar agendamento</Button>
                    </div>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <ScheduleAttendanceDataCard
                    id={id}
                    form={form}
                    reasonOptions={reasonOptions}
                    priorityOptions={priorityOptions}
                    channelOptions={channelOptions}
                    hasProcedure={hasProcedure}
                    onChangeField={setFormField}
                    onLoadProcedure={loadProcedure}
                    onToggleInactive={toggleInactive}
                />

                <ScheduleAttendanceScheduleCard
                    scheduleMode={scheduleMode}
                    onScheduleModeChange={setScheduleMode}
                    selectedDates={selectedDates}
                    onSelectedDatesChange={setSelectedDates}
                    dateHour={dateHour}
                    onDateHourChange={setDateHour}
                    dateList={dateList}
                    onRemoveDate={removeDate}
                    onAddSelectedDates={addSelectedDates}
                    recurrence={recurrence}
                    onChangeRecurrence={setRecurrenceField}
                    weekdayOptions={weekdayOptions}
                />

                <RegisteredLocation
                    title="Endereço do fato"
                    data={address}
                    setData={setAddress}
                    showSectors
                    storePlaceName
                    coordinatesRequired={false}
                />

                <Dropzone
                    title="Anexos"
                    attaches={attaches}
                    setAttaches={setAttaches}
                    attachesDescription={attachesDescription}
                    setAttachesDescription={setAttachesDescription}
                />

                <ScheduleAttendanceAttendanceCard
                    attendanceList={attendanceList}
                    boName={optionsData.boName}
                />

                <ScheduleAttendanceActionsCard
                    id={id}
                    isSaving={isSaving}
                    onDelete={destroy}
                    onSaveAndCopy={() => persist(false, true)}
                    onSaveAndAdd={() => persist(true)}
                    onSave={() => persist(false)}
                />
            </Template.Body>

            <ScheduleAttendanceProcedureModal
                open={procedureModal.open}
                content={procedureModal.content}
                onOpenChange={(open) => setProcedureModal(prev => ({...prev, open}))}
                onCopy={copyProcedureToDescription}
            />
        </Template>
    );
};

export default ScheduleAttendanceForm;